// "Push pending" / "Sync payments" action surface (BUILD_SPEC.md §12, goal
// G17) — services/api/src/modules/xero/sync.ts's POST /xero/push and POST
// /xero/sync/payments. Both no-op cleanly (`enabled: false` + a plain-language
// message) when the connection isn't CONNECTED, matching §12's "system fully
// functional before credentials exist" — this surface never looks broken while
// PENDING, it just explains there is nothing to sync yet.
import { Alert, Badge, Button, Group, List, Paper, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconRotateClockwise2 } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { useCanSyncXero } from '../permissions';
import { usePushPendingToXero, useSyncXeroPayments } from '../api';

// Aligned to the theme's semantic palette (theme.ts: brandGreen=success,
// brandRed=error/blocked — branded-neutral ruling, 2026-08-24). ALREADY_SYNCED
// is still a clean outcome (nothing needed doing), so it reads as success too.
const OUTCOME_COLOR: Record<string, string> = { SYNCED: 'brandGreen', ALREADY_SYNCED: 'brandGreen', ERROR: 'brandRed' };

export function XeroSyncActions() {
  const canSync = useCanSyncXero();
  const push = usePushPendingToXero();
  const pull = useSyncXeroPayments();

  if (!canSync) {
    return (
      <Paper withBorder radius="md" p="lg">
        <Text size="sm" c="dimmed">
          SALES_ACCOUNTS or ADMIN role is required to push or sync with Xero.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="md">
        <Title order={3}>Sync</Title>
        <Group>
          <Tooltip label="Push: sends every approved sales invoice, supplier bill and credit note that hasn't reached Xero yet.">
            <Button leftSection={<IconRefresh size={16} />} onClick={() => push.mutate()} loading={push.isPending}>
              Push pending
            </Button>
          </Tooltip>
          <Tooltip label="Pull: checks Xero for payments against invoices already synced, and marks them PAID here.">
            <Button variant="light" leftSection={<IconRotateClockwise2 size={16} />} onClick={() => pull.mutate()} loading={pull.isPending}>
              Sync payments
            </Button>
          </Tooltip>
        </Group>

        {push.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {push.error instanceof ApiRequestError ? push.error.message : 'Push failed.'}
          </Alert>
        )}
        {push.data && !push.data.enabled && (
          <Alert color="gray" variant="light">
            {push.data.message}
          </Alert>
        )}
        {push.data && push.data.enabled && (
          <div>
            <Text size="sm" fw={500} mb={4}>
              Pushed {push.data.results.length} document{push.data.results.length === 1 ? '' : 's'}
            </Text>
            {push.data.results.length > 0 && (
              <List size="sm" spacing={4}>
                {push.data.results.map((r, i) => (
                  <List.Item key={i}>
                    {r.entityType} {r.entityId}{' '}
                    <Badge size="xs" color={OUTCOME_COLOR[r.outcome]} variant="light">
                      {r.outcome}
                    </Badge>
                    {r.error && (
                      <Text span c="red" size="xs">
                        {' '}
                        — {r.error}
                      </Text>
                    )}
                  </List.Item>
                ))}
              </List>
            )}
          </div>
        )}

        {pull.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {pull.error instanceof ApiRequestError ? pull.error.message : 'Payment sync failed.'}
          </Alert>
        )}
        {pull.data && !pull.data.enabled && (
          <Alert color="gray" variant="light">
            {pull.data.message}
          </Alert>
        )}
        {pull.data && pull.data.enabled && (
          <div>
            <Text size="sm" fw={500} mb={4}>
              Checked {pull.data.results.length} invoice{pull.data.results.length === 1 ? '' : 's'}
            </Text>
            {pull.data.results.length > 0 && (
              <List size="sm" spacing={4}>
                {pull.data.results.map((r) => (
                  <List.Item key={r.invoiceId}>
                    Invoice {r.invoiceId}: {r.previousStatus} → {r.newStatus}
                    {r.paid && (
                      <Badge size="xs" color="brandGreen" variant="light" ml={6}>
                        PAID
                      </Badge>
                    )}
                  </List.Item>
                ))}
              </List>
            )}
          </div>
        )}
      </Stack>
    </Paper>
  );
}
